import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import {
  addPolicyService,
  deletePolicyService,
  getPolicyByIdService,
  getPolicyListService,
  getPolicyTypesService,
  updatePolicyService,
} from '../../services/legal.services';

const initialState = {
  policyTypesList: [],
  policyList: {
    totalCount: 0,
    totalPage: 0,
    policyData: [],
  },
  policyDetails: null,
};

export const getPolicyTypes = createAsyncThunk(
  `GET_POLICY_TYPES`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await getPolicyTypesService();
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getPolicyList = createAsyncThunk(
  `GET_POLICY`,
  async ({ page, limit, sortField, sortType, search }, { rejectWithValue }) => {
    try {
      const response = await getPolicyListService(
        page,
        limit,
        sortField,
        sortType,
        search
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const addPolicy = createAsyncThunk(
  'ADD_POLICY',
  async (body, { rejectWithValue }) => {
    try {
      const response = await addPolicyService(body);
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getPolicyById = createAsyncThunk(
  'GET_POLICY_BY_ID',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getPolicyByIdService(id);
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const updatePolicy = createAsyncThunk(
  'UPDATE_POLICY_',
  async (body, { rejectWithValue }) => {
    try {
      const response = await updatePolicyService(body);
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const deletePolicy = createAsyncThunk(
  'DELETE_POLICY',
  async (id, { rejectWithValue }) => {
    try {
      const response = await deletePolicyService(id);
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

const legalSlice = createSlice({
  name: 'legalSlice',
  initialState,
  reducers: {
    resetPolicies: (state) => {
      state.policyList = {
        totalCount: 0,
        totalPage: 0,
        policyData: [],
      };
    },
    resetPolicyDetails: (state) => {
      state.policyDetails = null;
    },
  },
  extraReducers: (builder) => {
    // Get Policy Types
    builder
      .addCase(getPolicyTypes.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { policyList } = payload?.data?.data;
          state.policyTypesList = [...policyList];
        }
      })
      .addCase(getPolicyTypes.rejected, (state, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Get Policy List
    builder
      .addCase(getPolicyList.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, legalPolicy } = payload?.data?.data;
          state.policyList = {
            totalCount: count,
            totalPage,
            policyData: [...legalPolicy],
          };
        }
      })
      .addCase(getPolicyList.rejected, (state, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Add Policy Type
    builder
      .addCase(addPolicy.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          toast.success(`${payload?.data?.message}`);
        }
      })
      .addCase(addPolicy.rejected, (state, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Get Policy Type Details
    builder
      .addCase(getPolicyById.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          state.policyDetails = payload?.data?.data;
        }
      })
      .addCase(getPolicyById.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Update Policy
    builder
      .addCase(updatePolicy.fulfilled, (_, { payload }) => {
        if (!payload?.data?.isError) {
          toast.success(`${payload?.data?.message}`);
        }
      })
      .addCase(updatePolicy.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Delete Policy
    builder
      .addCase(deletePolicy.fulfilled, (_, { payload }) => {
        if (!payload?.data?.isError) {
          toast.success(`${payload?.data?.message}`);
        }
      })
      .addCase(deletePolicy.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });
  },
});

export const { resetPolicies, resetPolicyDetails } = legalSlice.actions;

export default legalSlice.reducer;
